import * as React from "react";
import {
  Avatar,
  Box,
  Chip,
  Divider,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import EmailIcon         from "@mui/icons-material/Email";
import PhoneIcon         from "@mui/icons-material/Phone";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PaymentIcon       from "@mui/icons-material/Payment";
import HomeIcon          from "@mui/icons-material/Home";
import CheckCircleIcon   from "@mui/icons-material/CheckCircle";
import { MoneyRounded }  from "@mui/icons-material";

/**
 * TenantInfoBanner — Reusable tenant info card
 *
 * Props:
 *  tenant  = { tenantName, tenantEmail, tenantPhone, paymentDate, moveInDate, flatName, flatRent, flatAdvance }
 *  compact = bool — for use inside modals on colored backgrounds (default: false)
 */
export default function TenantInfoBanner({ tenant, compact = false }) {
  if (!tenant) return null;

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return "N/A";
    }
  };

  const infoItems = [
    { icon: <EmailIcon sx={{ fontSize: 20 }} />,         label: "Email Address",  value: tenant?.tenantEmail || "N/A" },
    { icon: <PhoneIcon sx={{ fontSize: 20 }} />,         label: "Contact Number", value: tenant?.tenantPhone || "N/A" },
    { icon: <PaymentIcon sx={{ fontSize: 20 }} />,       label: "Payment Date",   value: formatDate(tenant?.paymentDate) },
    { icon: <CalendarTodayIcon sx={{ fontSize: 20 }} />, label: "Move In Date",   value: formatDate(tenant?.moveInDate) },
    { icon: <HomeIcon sx={{ fontSize: 20 }} />,          label: "Flat Name",      value: tenant?.flatName || "N/A" },
    {
      icon: <MoneyRounded sx={{ fontSize: 20 }} />,
      label: "Rent / Advance",
      value:
        tenant?.flatRent && tenant?.flatAdvance
          ? `${tenant.flatRent} / ${tenant.flatAdvance}`
          : tenant?.flatRent || "N/A",
    },
  ];

  // ── compact = inside modal (white card on any background) ────────
  if (compact) {
    return (
      <Box
        sx={{
          background: "#ffffff",
          borderRadius: "14px",
          border: "1px solid #e2e8f0",
          overflow: "hidden",
          mb: 2,
          boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
        }}
      >
        {/* Blue top accent */}
        <Box sx={{ height: 3, background: "linear-gradient(90deg, #2563eb 0%, #1e40af 100%)" }} />

        <Box sx={{ p: 2.5 }}>
          {/* Avatar row */}
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
            <Avatar
              sx={{
                width: 50,
                height: 50,
                fontSize: "1.3rem",
                fontWeight: 800,
                background: "linear-gradient(135deg, #2563eb 0%, #1e40af 100%)",
                boxShadow: "0 4px 14px rgba(37,99,235,0.3)",
                border: "3px solid #fff",
                flexShrink: 0,
              }}
            >
              {tenant?.tenantName?.charAt(0)?.toUpperCase() || "T"}
            </Avatar>
            <Box>
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: "1.1rem",
                  color: "#0f172a",
                  lineHeight: 1.2,
                  mb: 0.4,
                  fontFamily: "'Space Grotesk', sans-serif",
                }}
              >
                {tenant?.tenantName || "Tenant"}
              </Typography>
              <Chip
                icon={<CheckCircleIcon sx={{ fontSize: "13px !important", color: "#fff !important" }} />}
                label="Active Tenant"
                size="small"
                sx={{
                  background: "#10b981",
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: "0.72rem",
                  height: 22,
                  "& .MuiChip-icon": { color: "#fff" },
                }}
              />
            </Box>
          </Stack>

          <Divider sx={{ borderColor: "#e2e8f0", mb: 2 }} />

          {/* Info grid */}
          <Grid container spacing={1.5}>
            {infoItems.map((item, idx) => (
              <Grid item xs={12} sm={6} key={idx}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    p: "10px 12px",
                    borderRadius: "10px",
                    background: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    transition: "all 0.2s",
                    "&:hover": {
                      borderColor: "#2563eb",
                      boxShadow: "0 2px 8px rgba(37,99,235,0.1)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 34,
                      height: 34,
                      borderRadius: "8px",
                      background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      color: "#2563eb",
                      transition: "all 0.2s",
                      ".info-item-box:hover &": {
                        background: "linear-gradient(135deg, #2563eb 0%, #1e40af 100%)",
                        color: "#fff",
                      },
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        display: "block",
                        fontSize: "0.65rem",
                        color: "#64748b",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        mb: 0.2,
                      }}
                    >
                      {item.label}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.8rem",
                        fontWeight: 600,
                        color: "#0f172a",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {item.value}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>
    );
  }

  // ── default = full page (uses LandlordFunctionality.css classes) ─
  return (
    <Box
      className="tenant-info-card"
      sx={{ background: "#fff", borderRadius: "20px", border: "1px solid #e2e8f0", mb: 4, overflow: "hidden", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}
    >
      <Box className="tenant-card-inner">
        {/* Avatar + Name */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={3}
          sx={{ mb: 4 }}
        >
          <Avatar className="tenant-avatar">
            {tenant?.tenantName?.charAt(0)?.toUpperCase() || "T"}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" className="tenant-name">
              {tenant?.tenantName || "Tenant"}
            </Typography>
            <Chip
              icon={<CheckCircleIcon />}
              label="Active Tenant"
              className="status-chip"
              size="small"
            />
          </Box>
        </Stack>

        <Divider className="info-divider" />

        {/* Info Grid */}
        <Grid container spacing={2.5} sx={{ mt: 1 }}>
          {infoItems.map((item, idx) => (
            <Grid item xs={12} sm={6} lg={4} key={idx}>
              <Box className="info-item">
                <Box className="info-icon-wrapper">
                  {item.icon}
                </Box>
                <Box className="info-content">
                  <Typography variant="caption" className="info-label">
                    {item.label}
                  </Typography>
                  <Typography variant="body2" className="info-value" noWrap>
                    {item.value}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
}